import { motion, useAnimation, useMotionValue } from 'framer-motion';
import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import './Testimonials.css';

function TestimonialColumn({ data, duration, delay, activeCardId, setActiveCardId }) {
    const controls = useAnimation();
    const y = useMotionValue(0);
    const maxScroll = -300 * data.length;
    const [wasPaused, setWasPaused] = useState(false);

    useEffect(() => {
        if (activeCardId === null && wasPaused) {
            controls.start({ y: 0, transition: { duration: 0.5, ease: "easeInOut" } })
                .then(() => {
                    controls.start({
                        y: maxScroll,
                        transition: { duration, ease: "linear", repeat: Infinity, repeatType: "loop", delay }
                    });
                });
            setWasPaused(false);
        }
        else if (activeCardId === null) {
            controls.start({
                y: maxScroll,
                transition: { duration, ease: "linear", repeat: Infinity, repeatType: "loop", delay }
            });
        }
        else {
            setWasPaused(true);
            controls.stop();
        }
    }, [activeCardId]);

    return (
        <div className="testimonial-column">
            <motion.div className="testimonials-carousel" style={{ y }} animate={controls}>
                {[...data, ...data].map((testimonial, index) => (
                    <button
                        key={`${testimonial.id}-${index}`}
                        className="testimonial-card"
                        type="button"
                        onClick={() => setActiveCardId(activeCardId === testimonial.id ? null : testimonial.id)}
                        aria-pressed={activeCardId === testimonial.id}
                        style={{
                            opacity: activeCardId === null || activeCardId === testimonial.id ? 1 : 0.5
                        }}
                    >
                        <div className="quote-icon">❝</div>
                        <p className="testimonial-quote">{testimonial.quote}</p>
                        <div className="testimonial-author">
                            <h4 className="author-name">{testimonial.name}</h4>
                            <p className="author-title">{testimonial.title}</p>
                        </div>
                    </button>
                ))}
            </motion.div>
        </div>
    );
}

TestimonialColumn.propTypes = {
    data: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.number.isRequired,
        quote: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired,
        title: PropTypes.string.isRequired
    })).isRequired,
    duration: PropTypes.number.isRequired,
    delay: PropTypes.number.isRequired,
    activeCardId: PropTypes.number,
    setActiveCardId: PropTypes.func.isRequired
};


// --- Main App Component ---
export default function App() {
    const [activeCardId, setActiveCardId] = useState(null);

    // Data for the four columns
    const leftColumnData = [
        { id: 1, quote: "Adflyn didn't just improve our social media presence-it became our primary lead generation engine. We're now generating 400+ qualified leads per month from social channels that previously produced minimal results. The AI agents handle everything from content to initial prospect conversations.", name: "Sarah Chen", title: "VP of Marketing, TechScale Solutions" },
        { id: 2, quote: "Client retention is up 45% because we're driving measurable business outcomes, not just vanity metrics.", name: "Andrew Gard", title: "CEO, Marketing Dynamics Agency" },
        { id: 3, quote: "The lead generation capabilities alone have paid for Adflyn 10x over. Our sales team receives a steady stream of qualified prospects who are already engaged with our brand. Cost per lead dropped by 65% while lead quality improved significantly.", name: "Marcus Thompson", title: "Enterprise SaaS Co." },
        { id: 4, quote: "The analytics dashboard gives us insights we never had before. We can now make data-driven decisions with confidence.", name: "Sarah Johnson", title: "Digital Manager" },
    ];

    const centerColumnData = [
        { id: 5, quote: "Implementation was seamless and the support team was incredibly helpful throughout the entire process.", name: "Michael Chen", title: "CTO TechStart" },
        { id: 6, quote: "Our ROI improved by 300% within the first quarter of using Adflyn. The results speak for themselves.", name: "Emily Rodriguez", title: "Growth Manager" },
        { id: 7, quote: "The AI-driven insights have transformed how we approach our marketing campaigns. Absolutely game-changing.", name: "David Wilson", title: "Marketing VP" },
        { id: 8, quote: "Customer acquisition costs dropped significantly while our conversion rates soared. Couldn't be happier.", name: "Lisa Park", title: "Performance Lead" },
    ];

    const rightColumnData = [
        { id: 9, quote: "The real-time optimization features have saved us countless hours while improving our campaign performance.", name: "James Miller", title: "Ad Operations" },
        { id: 10, quote: "Adflyn's targeting capabilities are unmatched. We're reaching exactly the right audience every time.", name: "Anna Thompson", title: "Media Buyer" },
        { id: 11, quote: "The platform scales beautifully with our growing business needs. It's built for the future.", name: "Robert Davis", title: "Head of Growth" },
        { id: 12, quote: "Integration with our existing tools was flawless. Everything just works together perfectly.", name: "Maria Garcia", title: "Operations Manager" },
    ];

    const farRightColumnData = [
        { id: 13, quote: "The user interface is intuitive and our team was up and running in no time. Excellent user experience.", name: "Tom Wilson", title: "UX Director" },
        { id: 14, quote: "Adflyn's customer support is outstanding. They're always there when we need them most.", name: "Rachel Green", title: "Account Manager" },
        { id: 15, quote: "The reporting features give us complete transparency into our campaign performance. Data we can trust.", name: "Kevin Brown", title: "Analytics Lead" },
        { id: 16, quote: "Cost-effective solution that delivers premium results. Best investment we've made for our marketing stack.", name: "Diana Lee", title: "CMO" },
    ];

    return (
        <div>
            <section id="testimonials" className="testimonials-section-bg">
                <div className="testimonials-header">
                    <p className="feature-pill">
                        Testimonials
                    </p>
                    <h2 className="header-title">
                        <span className="quote-mark">"</span>
                       From Social Media to Sales Pipeline <br/> - Real Results
                        <span className="quote-mark">"</span>
                    </h2>
                    <p className="header-subtitle">
                        Companies worldwide can use Adflyn to transform social media activity into qualified leads and measurable revenue growth.
                    </p>
                </div>

                {/* Responsive Carousel Container */}
                <div 
                    className="testimonials-carousel-container"
                >
                    {/* Column 1: Always visible */}
                    <TestimonialColumn 
                        data={leftColumnData} 
                        duration={leftColumnData.length * 4} 
                        activeCardId={activeCardId} 
                        setActiveCardId={setActiveCardId} 
                        delay={0}
                    />
                    
                    {/* Column 2: Visible from 640px upwards */}
                    <TestimonialColumn 
                        data={centerColumnData} 
                        duration={centerColumnData.length * 4.5} 
                        delay={1.5} 
                        activeCardId={activeCardId} 
                        setActiveCardId={setActiveCardId} 
                    />
                    
                    {/* Column 3: Visible from 1024px upwards */}
                    <TestimonialColumn 
                        data={rightColumnData} 
                        duration={rightColumnData.length * 5} 
                        delay={3} 
                        activeCardId={activeCardId} 
                        setActiveCardId={setActiveCardId} 
                    />
                    
                    {/* Column 4: Visible from 1024px upwards */}
                    <TestimonialColumn 
                        data={farRightColumnData} 
                        duration={farRightColumnData.length * 5.5} 
                        delay={4} 
                        activeCardId={activeCardId} 
                        setActiveCardId={setActiveCardId} 
                    />
                </div>
            </section>
        </div>
    );
}
